import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Bot, Copy, Eye, Send } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Tag } from "@/components/common/StatusChip";
import RetellRequests from "./RetellRequests";

function Row({ label, children }) {
  return (<><dt className="text-muted-foreground">{label}</dt><dd className="mono break-all">{children}</dd></>);
}

function Copyable({ value, testId }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span>{value}</span>
      <button type="button" className="text-muted-foreground hover:text-foreground" title="Copy" onClick={() => { navigator.clipboard?.writeText(value); toast.success("Copied"); }} data-testid={testId}><Copy className="h-3 w-3" /></button>
    </span>
  );
}

export default function RetellPanel() {
  const qc = useQueryClient();
  const { data: cfg } = useQuery({ queryKey: ["retell-config"], queryFn: () => api.get("/retell/config").then((r) => r.data) });
  const { data: health } = useQuery({ queryKey: ["retell-health"], queryFn: () => api.get("/retell/health").then((r) => r.data), refetchInterval: 30000 });
  const [recipient, setRecipient] = useState("");
  const [alerts, setAlerts] = useState("");
  const [preview, setPreview] = useState(null);
  const [confirmLive, setConfirmLive] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (cfg) { setRecipient(cfg.ticket_recipient || ""); setAlerts((cfg.alert_emails || []).join(", ")); } }, [cfg]);
  const refresh = () => { qc.invalidateQueries({ queryKey: ["retell-config"] }); qc.invalidateQueries({ queryKey: ["retell-health"] }); qc.invalidateQueries({ queryKey: ["retell-requests"] }); };
  const patch = async (body, ok) => { try { await api.patch("/retell/config", body); toast.success(ok); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const test = async (send, urgency = "routine") => {
    setBusy(true);
    try {
      const { data } = await api.post("/retell/test", { send, urgency });
      if (data.preview) setPreview(data); else { toast.success(`Test request ${data.reference}: ${data.status}`); setPreview(null); refresh(); }
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!cfg) return null;
  const live = cfg.live_enabled;
  return (
    <div className="surface p-4" data-testid="retell-panel">
      <div className="flex flex-wrap items-center gap-2">
        <Bot className="h-4 w-4 text-primary" />
        <span className="text-sm font-bold">Ava after-hours bridge (Retell AI)</span>
        <Tag tone={live ? "emerald" : "amber"} data-testid="retell-live-tag">{live ? "live" : "held — nothing is sent"}</Tag>
        <label className="ml-auto flex items-center gap-2 text-xs"><span>Live sending</span>
          <Switch checked={live} onCheckedChange={(v) => (v ? setConfirmLive(true) : patch({ live_enabled: false }, "Live sending OFF — requests are held"))} data-testid="retell-live-switch" /></label>
      </div>
      <AlertDialog open={confirmLive} onOpenChange={setConfirmLive}>
        <AlertDialogContent data-testid="retell-live-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Turn on live sending?</AlertDialogTitle>
            <AlertDialogDescription>From now on every request Ava files will be emailed to <span className="mono">{cfg.ticket_recipient}</span> and become a real ticket. Requests currently held are not released automatically — use "send" on each row if you want them.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="retell-live-cancel-btn">Keep held</AlertDialogCancel>
            <AlertDialogAction onClick={() => patch({ live_enabled: true }, "Live sending ON — Ava's tickets will be emailed")} data-testid="retell-live-confirm-btn">Go live</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <p className="mt-1 text-[11px] text-muted-foreground">Ava's custom function posts here; the bridge validates and redacts the request, emails the ticket address through the fail-proof outbox, and only reports "confirmed" once the ticket actually exists.</p>
      <dl className="mt-3 grid gap-1 text-xs sm:grid-cols-[170px_1fr]">
        <Row label="Signature key">{cfg.api_key_set ? <Tag tone="emerald">RETELL_API_KEY set</Tag> : <Tag tone="red">RETELL_API_KEY missing — lifecycle webhooks rejected</Tag>}</Row>
        <Row label="Function secret">{cfg.function_secret_set ? <Tag tone="emerald">{cfg.function_header} configured</Tag> : <Tag tone="amber">RETELL_FUNCTION_SECRET missing</Tag>}</Row>
        <Row label="Mail provider">{health?.configured?.mail_provider ? <Tag tone="emerald">ready</Tag> : <Tag tone="red">no provider</Tag>}</Row>
        <Row label="Custom function URL"><Copyable value={cfg.endpoints.tickets} testId="retell-copy-tickets-url" /></Row>
        <Row label="Lifecycle webhook URL"><Copyable value={cfg.endpoints.events} testId="retell-copy-events-url" /></Row>
        <Row label="Health">{cfg.endpoints.health}</Row>
      </dl>
      <form className="mt-3 grid gap-2 sm:grid-cols-2" onSubmit={(e) => { e.preventDefault(); patch({ ticket_recipient: recipient, alert_emails: alerts.split(",").map((s) => s.trim()).filter(Boolean) }, "Bridge settings saved"); }}>
        <label className="text-xs"><span className="eyebrow">Ticket recipient</span><Input value={recipient} onChange={(e) => setRecipient(e.target.value)} className="mono mt-1 text-xs" data-testid="retell-recipient-input" /></label>
        <label className="text-xs"><span className="eyebrow">Alert emails (blank = all admins)</span><Input value={alerts} onChange={(e) => setAlerts(e.target.value)} placeholder="ops@…, lead@…" className="mono mt-1 text-xs" data-testid="retell-alerts-input" /></label>
        <div className="flex flex-wrap gap-2 sm:col-span-2">
          <Button type="submit" size="sm" data-testid="retell-save-btn">Save</Button>
          <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => test(false)} data-testid="retell-preview-btn"><Eye className="mr-1 h-3.5 w-3.5" /> Preview email</Button>
          <Button type="button" size="sm" variant="outline" disabled={busy || !live} title={live ? "Sends one clearly-marked test through the full pipeline" : "Turn on live sending first"} onClick={() => test(true)} data-testid="retell-send-test-btn"><Send className="mr-1 h-3.5 w-3.5" /> Send controlled test</Button>
        </div>
      </form>
      {preview && (
        <div className="mt-3 border border-border" data-testid="retell-preview">
          <div className="flex items-center justify-between border-b border-border bg-muted/40 px-3 py-1.5 text-xs"><span className="mono">{preview.subject}</span><button className="text-muted-foreground hover:underline" onClick={() => setPreview(null)}>close</button></div>
          <iframe title="Ava email preview" sandbox="" srcDoc={preview.html} className="h-[420px] w-full bg-white" />
        </div>
      )}
      <RetellRequests />
    </div>
  );
}
